import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const AUTH_KEY = 'b2b_auth_token';

// ─── 어드민 계정은 하드코딩 유지 (DB 불필요) ───
const ADMIN_CREDENTIALS: Record<string, string> = {
  'admin': 'admin1234',
};

export type UserRole = 'admin' | 'partner';

/**
 * 역할별 마진 배율
 * - admin  : 원가 그대로 (×1.0)
 * - partner: 원가 +10%  (×1.1)
 */
export const ROLE_MARGIN: Record<UserRole, number> = {
  admin:   1.0,
  partner: 1.1,
};

/**
 * 역할별 리베이트(커미션) 비율
 * - admin  : 해당 없음 (0)
 * - partner: 기본 리베이트에서 5% 차감 적용
 *   예) 상품 리베이트 25% → 파트너 실수령 20%
 *       상품 리베이트 20% → 파트너 실수령 20% (그대로)
 */
export const PARTNER_REBATE_DEDUCTION = 5; // 파트너 리베이트 차감 %

export function calcPartnerRebate(baseRebate: number): number {
  return Math.max(0, baseRebate - PARTNER_REBATE_DEDUCTION);
}

export function applyMargin(price: number, role: UserRole): number {
  if (price <= 0) return price;
  return Math.round(price * ROLE_MARGIN[role] * 10) / 10;
}

// 전역 상태 - 모든 컴포넌트가 공유
type AuthState = { isLoggedIn: boolean; username: string | null; role: UserRole | null; partnerKey: string | null };
type Listener = (state: AuthState) => void;

const listeners = new Set<Listener>();

function getStoredAuth(): AuthState {
  try {
    const stored = sessionStorage.getItem(AUTH_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // 구버전 role(buyer/vip) 처리: admin으로 fallback
      const role: UserRole = (parsed.role === 'admin' || parsed.role === 'partner') ? parsed.role : 'admin';
      return { isLoggedIn: true, username: parsed.username, role, partnerKey: parsed.partnerKey ?? null };
    }
  } catch { /* ignore */ }
  return { isLoggedIn: false, username: null, role: null, partnerKey: null };
}

/** 현재 로그인한 파트너의 실제 username(partner_key) 반환 */
export function getPartnerKey(): string | null {
  try {
    const stored = sessionStorage.getItem(AUTH_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.partnerKey ?? null;
    }
  } catch { /* ignore */ }
  return null;
}

let globalAuth: AuthState = getStoredAuth();


function setGlobalAuth(next: AuthState) {
  globalAuth = next;
  listeners.forEach(fn => fn(next));
}

export function useAuth() {
  const [auth, setAuth] = useState<AuthState>(globalAuth);

  useEffect(() => {
    const handler = (state: AuthState) => setAuth({ ...state });
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    // 1. 어드민 계정 체크 (하드코딩)
    if (ADMIN_CREDENTIALS[username] && ADMIN_CREDENTIALS[username] === password) {
      const next: AuthState = { isLoggedIn: true, username, role: 'admin', partnerKey: null };
      sessionStorage.setItem(AUTH_KEY, JSON.stringify({ username, role: 'admin', partnerKey: null }));
      setGlobalAuth(next);
      return true;
    }
    // 2. 파트너 계정 체크 (DB)
    const { data } = await supabase
      .from('partner_accounts')
      .select('username, password, display_name, status')
      .eq('username', username)
      .eq('password', password)
      .maybeSingle();

    if (data) {
      if (data.status === 'inactive') return false; // 비활성 계정 차단
      const displayName = data.display_name || username;
      // partnerKey = 실제 DB username (주문 구분용)
      const next: AuthState = { isLoggedIn: true, username: displayName, role: 'partner', partnerKey: username };
      sessionStorage.setItem(AUTH_KEY, JSON.stringify({ username: displayName, role: 'partner', partnerKey: username }));
      setGlobalAuth(next);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(AUTH_KEY);
    setGlobalAuth({ isLoggedIn: false, username: null, role: null, partnerKey: null });
  }, []);

  return { ...auth, login, logout };
}
